/**
 * DevelopmentStage — LEFT column while stage === DEVELOPMENT.
 *
 * The operator authors Hook / Script Body / CTA / Video Format. Fields
 * autosave (debounced ~800ms + on blur) via PATCH /draft. Nothing here ever
 * calls chat/LLM — typing only saves the draft. "Copy to field" from the
 * chat is delivered through the shell and flows through the same autosave.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useAutosave, SaveIndicator } from './useCcsApi';
import ContextPanel from './ContextPanel';

/**
 * An error, as text that React can actually render.
 *
 * WHY THIS EXISTS. `handleProceed` used to do
 * `Array.isArray(detail) ? detail.join(', ') : (detail || fallback)`. FastAPI's
 * `HTTPException(detail=...)` accepts any JSON, and the proceed gate sent
 * `{error, missing}` — an object, so neither branch caught it, and the object
 * went into React state and then into a <p>. React error #31 took down the
 * whole Ads & Creative tab, every time, for every session.
 *
 * A screen must never crash because of the SHAPE of an error. That is rule 10's
 * territory: a failure has to stay distinguishable from a normal result, and a
 * blank crashed tab tells the operator nothing about what was wrong. So this
 * accepts anything and always returns a string.
 */
export function errorText(e, fallback) {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => (typeof d === 'string' ? d : d?.msg || d?.error || null))
      .filter(Boolean);
    if (parts.length) return parts.join(', ');
  }
  if (detail && typeof detail === 'object') {
    // The shape that caused the crash. Read it rather than render it.
    const head = typeof detail.error === 'string' ? detail.error : null;
    const missing = Array.isArray(detail.missing) ? detail.missing.join(', ') : null;
    if (head && missing) return `${head}: ${missing}`;
    if (head) return head;
    try {
      return JSON.stringify(detail);
    } catch (err) {
      return fallback;
    }
  }
  if (typeof e?.message === 'string' && e.message.trim()) return e.message;
  return fallback;
}

export default function DevelopmentStage({
  session,
  api,
  applySession,
  setCopyHandler,
}) {
  const id = session.session_id;
  const draft = session.draft || {};

  const [hook, setHook] = useState(draft.hook || '');
  const [scriptBody, setScriptBody] = useState(draft.script_body || '');
  const [cta, setCta] = useState(draft.cta || '');
  const [videoFormat, setVideoFormat] = useState(draft.video_format || '');
  // V2-CREATIVE-WAVE1: the operator picks the creative type. It used to be the
  // constant `config.TRELLO_CREATIVE_TYPE` — "VE" — for every session, which is
  // why a Custom session that was really AI Video was still named `...-VE-...`.
  const [creativeType, setCreativeType] = useState(draft.creative_type || '');
  const [proceedError, setProceedError] = useState(null);
  const [proceeding, setProceeding] = useState(false);
  const [copyConflict, setCopyConflict] = useState(null); // { key, text }

  const formatOptions = session.video_format_options || [];
  // From the server, not a second copy here: `update_draft` validates against
  // the same list, so a control offering a value the server rejects is
  // impossible by construction.
  const creativeTypeOptions = session.creative_type_options || [];

  const saveFn = useCallback((patch) => api.saveDraft(id, patch), [api, id]);
  const { status, schedule, flush, saveNow, retry } = useAutosave(saveFn);

  const full = useCallback(
    (over) => ({ hook, script_body: scriptBody, cta, video_format: videoFormat,
                 creative_type: creativeType, ...over }),
    [hook, scriptBody, cta, videoFormat, creativeType],
  );

  const onHook = (v) => { setHook(v); schedule(full({ hook: v })); };
  const onBody = (v) => { setScriptBody(v); schedule(full({ script_body: v })); };
  const onCta = (v) => { setCta(v); schedule(full({ cta: v })); };
  // A <select> has nothing to debounce — there is no typing, and no blur that
  // would flush it. Debouncing one only creates an 800 ms window in which the
  // choice can be lost, which is exactly what happened live on 2026-09-08. The
  // comment below used to say "save immediately" while calling `schedule`;
  // it now does what it says.
  const onFormat = (v) => {
    setVideoFormat(v);
    saveNow(full({ video_format: v }));
  };
  const onCreativeType = (v) => {
    setCreativeType(v);
    saveNow(full({ creative_type: v }));
  };

  // ---- Copy-to-field (operator action routed from the persistent chat) ----
  const applyCopy = useCallback((key, text, mode) => {
    const currents = { hook, script_body: scriptBody, cta };
    const setters = { hook: setHook, script_body: setScriptBody, cta: setCta };
    const setter = setters[key];
    if (!setter) return;
    const next = mode === 'append' && currents[key] ? `${currents[key]}\n${text}` : text;
    setter(next);
    schedule(full({ [key]: next }));
  }, [hook, scriptBody, cta, schedule, full]);

  const requestCopy = useCallback((key, text) => {
    const currents = { hook, script_body: scriptBody, cta };
    if ((currents[key] || '').trim()) setCopyConflict({ key, text });
    else applyCopy(key, text, 'replace');
  }, [hook, scriptBody, cta, applyCopy]);

  useEffect(() => {
    setCopyHandler(requestCopy);
    return () => setCopyHandler(null);
  }, [requestCopy, setCopyHandler]);

  // ---- Proceed gating ----
  const filled = {
    hook: !!hook.trim(),
    body: !!scriptBody.trim(),
    cta: !!cta.trim(),
    format: !!videoFormat,
    type: !!creativeType,
  };
  const missing = [
    !filled.hook && 'Hook',
    !filled.body && 'Script Body',
    !filled.cta && 'CTA',
    !filled.format && 'Video Format',
    !filled.type && 'Creative Type',
  ].filter(Boolean);
  const allFilled = missing.length === 0;

  const handleProceed = async () => {
    flush();
    setProceeding(true);
    setProceedError(null);
    try {
      const s = await api.proceed(id);
      applySession(s);
    } catch (e) {
      setProceedError(errorText(e, 'Could not proceed to review.'));
    } finally {
      setProceeding(false);
    }
  };

  const conflictLabel = copyConflict
    ? ({ hook: 'Hook', script_body: 'Script Body', cta: 'CTA' })[copyConflict.key]
    : '';

  return (
    <div className="space-y-4" data-testid="ccs-development-stage">
      <ContextPanel sessionId={id} api={api} />

      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white">Development</h3>
          <SaveIndicator status={status} onRetry={retry} />
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-400">Hook</label>
          <textarea
            value={hook}
            onChange={(e) => onHook(e.target.value)}
            onBlur={flush}
            rows={2}
            placeholder="Opening line that stops the scroll…"
            className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none resize-y"
            data-testid="ccs-field-hook"
          />
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-400">Script Body</label>
          <textarea
            value={scriptBody}
            onChange={(e) => onBody(e.target.value)}
            onBlur={flush}
            rows={7}
            placeholder="The body of the script…"
            className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none resize-y"
            data-testid="ccs-field-body"
          />
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-400">CTA</label>
          <textarea
            value={cta}
            onChange={(e) => onCta(e.target.value)}
            onBlur={flush}
            rows={2}
            placeholder="Call to action…"
            className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none resize-y"
            data-testid="ccs-field-cta"
          />
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-400">Creative Type</label>
          <select
            value={creativeType}
            onChange={(e) => onCreativeType(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:border-emerald-500 focus:outline-none"
            data-testid="ccs-field-creative-type"
          >
            <option value="">Select a type…</option>
            {creativeTypeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {/* Rule 10, before the operator commits rather than only after —
              the same warning the Autonomous path shows for this choice. */}
          {creativeType === 'AI:COMFY' && (
            <p className="text-xs text-amber-400" data-testid="ccs-comfy-not-connected">
              AI VIDEO COMFY is not connected yet — nothing will be generated or
              sent. Choose AI VIDEO TRELLO to send this brief to the editor.
            </p>
          )}
          {creativeType.startsWith('AI') && (
            <p className="text-xs text-slate-500" data-testid="ccs-ai-brief-notice">
              This session will produce an AI video generation brief, not a
              video-editor brief.
            </p>
          )}
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-400">Video Format</label>
          <select
            value={videoFormat}
            onChange={(e) => onFormat(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:border-emerald-500 focus:outline-none"
            data-testid="ccs-field-format"
          >
            <option value="">Select a format…</option>
            {videoFormat && !formatOptions.includes(videoFormat) && (
              <option value={videoFormat}>{videoFormat}</option>
            )}
            {formatOptions.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Checklist */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4" data-testid="ccs-checklist">
        <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Ready to review</h4>
        <div className="grid grid-cols-2 gap-2">
          {[
            ['Hook', filled.hook],
            ['Script Body', filled.body],
            ['CTA', filled.cta],
            ['Video Format', filled.format],
            ['Creative Type', filled.type],
          ].map(([label, ok]) => (
            <div key={label} className="flex items-center gap-2 text-sm">
              <span className={ok ? 'text-emerald-400' : 'text-slate-600'}>{ok ? '✓' : '○'}</span>
              <span className={ok ? 'text-slate-200' : 'text-slate-500'}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {proceedError && (
        <p className="text-xs text-rose-400" data-testid="ccs-proceed-error">{proceedError}</p>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleProceed}
          disabled={!allFilled || proceeding}
          title={allFilled ? 'Proceed to review' : `Missing: ${missing.join(', ')}`}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 transition-colors text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="ccs-proceed-btn"
        >
          {proceeding ? 'Proceeding…' : 'Proceed to Review'}
        </button>
      </div>

      {/* Copy-to-field replace/append prompt */}
      {copyConflict && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="ccs-copy-conflict">
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 max-w-sm w-full space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-white">{conflictLabel} already has content</h4>
              <p className="text-xs text-slate-400 mt-1">Replace it with the copied text, or append below?</p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCopyConflict(null)}
                className="px-3 py-1.5 text-sm bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600"
                data-testid="ccs-copy-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { applyCopy(copyConflict.key, copyConflict.text, 'append'); setCopyConflict(null); }}
                className="px-3 py-1.5 text-sm bg-slate-600 text-white rounded-lg hover:bg-slate-500"
                data-testid="ccs-copy-append"
              >
                Append
              </button>
              <button
                type="button"
                onClick={() => { applyCopy(copyConflict.key, copyConflict.text, 'replace'); setCopyConflict(null); }}
                className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-500"
                data-testid="ccs-copy-replace"
              >
                Replace
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
